import React, { useEffect, useState } from 'react';
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, TextField, Typography,
} from '@mui/material';
import { appointmentService } from '../../services/appointment.service';
import { AppointmentResponseDto, AppointmentStatus } from '../../types/appointment.types';
import { toastService } from '../../services/toast.service';
import { RescheduleAppointmentDialog } from './RescheduleAppointmentDialog';

interface Props {
  appointmentId: number | null;
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]:   'Очікує підтвердження',
  [AppointmentStatus.CONFIRMED]: 'Підтверджено',
  [AppointmentStatus.CANCELLED]: 'Скасовано',
  [AppointmentStatus.COMPLETED]: 'Завершено',
};

const STATUS_COLORS: Record<AppointmentStatus, 'warning' | 'success' | 'error' | 'default' | 'primary'> = {
  [AppointmentStatus.PENDING]:   'warning',
  [AppointmentStatus.CONFIRMED]: 'primary',
  [AppointmentStatus.CANCELLED]: 'error',
  [AppointmentStatus.COMPLETED]: 'default',
};

const canCancel = (s: AppointmentStatus) =>
  s === AppointmentStatus.PENDING || s === AppointmentStatus.CONFIRMED;
// E1: reschedule лише для CONFIRMED. У PENDING — confirm або cancel з причиною.
const canReschedule = (s: AppointmentStatus) => s === AppointmentStatus.CONFIRMED;

export const AppointmentManageDialog: React.FC<Props> = ({ appointmentId, open, onClose, onUpdate }) => {
  const [appointment, setAppointment] = useState<AppointmentResponseDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  useEffect(() => {
    if (!open || !appointmentId) return;
    setLoading(true);
    setAppointment(null);
    appointmentService.getAppointmentDetails(appointmentId)
      .then(setAppointment)
      .catch(() => toastService.error('Не вдалося завантажити деталі зустрічі.'))
      .finally(() => setLoading(false));
  }, [open, appointmentId]);

  const handleClose = () => {
    setCancelOpen(false);
    setCancelReason('');
    onClose();
  };

  const handleConfirmCancel = async () => {
    if (!appointment || !cancelReason.trim()) return;
    setCancelling(true);
    try {
      await appointmentService.cancelAppointment(appointment.id, { cancellationReason: cancelReason });
      toastService.success('Зустріч скасовано.');
      setCancelOpen(false);
      onUpdate?.();
      handleClose();
    } catch {
      toastService.error('Не вдалося скасувати зустріч.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Деталі зустрічі</DialogTitle>
        <DialogContent>
          {loading || !appointment ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar src={appointment.expert.avatar} sx={{ width: 44, height: 44 }}>
                    {appointment.expert.name.charAt(0)}
                  </Avatar>
                  <Typography variant="h6">{appointment.expert.name}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Дата</Typography>
                <Typography>{new Date(appointment.scheduledAt).toLocaleString('uk-UA')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Тривалість</Typography>
                <Typography>{appointment.durationMinutes} хв.</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Статус</Typography>
                <Chip
                  label={STATUS_LABELS[appointment.status]}
                  color={STATUS_COLORS[appointment.status]}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              {appointment.clientNotes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Ваші нотатки</Typography>
                  <Typography>{appointment.clientNotes}</Typography>
                </Grid>
              )}
              {appointment.expertNotes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Нотатки експерта</Typography>
                  <Typography>{appointment.expertNotes}</Typography>
                </Grid>
              )}
              {appointment.cancelledAt && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Скасовано</Typography>
                  <Typography>{new Date(appointment.cancelledAt).toLocaleString('uk-UA')}</Typography>
                </Grid>
              )}
              {appointment.cancellationReason && (
                <Grid item xs={appointment.cancelledAt ? 6 : 12}>
                  <Typography variant="body2" color="text.secondary">Причина скасування</Typography>
                  <Typography>{appointment.cancellationReason}</Typography>
                </Grid>
              )}

              {cancelOpen && (
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    label="Причина скасування"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!cancelOpen ? (
            <>
              <Button onClick={handleClose}>Закрити</Button>
              {appointment && canReschedule(appointment.status) && (
                <Button variant="outlined" onClick={() => setRescheduleOpen(true)}>
                  Перенести
                </Button>
              )}
              {appointment && canCancel(appointment.status) && (
                <Button color="error" onClick={() => setCancelOpen(true)}>
                  Скасувати зустріч
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={() => { setCancelOpen(false); setCancelReason(''); }} disabled={cancelling}>
                Назад
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleConfirmCancel}
                disabled={!cancelReason.trim() || cancelling}
              >
                Підтвердити скасування
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {appointment && rescheduleOpen && (
        <RescheduleAppointmentDialog
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          onSuccess={() => {
            setRescheduleOpen(false);
            onUpdate?.();
            handleClose();
          }}
          appointmentId={appointment.id}
          expertId={appointment.expert.id}
          expertName={appointment.expert.name}
        />
      )}
    </>
  );
};
